import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';

interface ServicesIntroData {
  title: string;
  highlight: string;
  description: string;
}

interface ServiceItem {
  icon: string;
  title: string;
  description: string;
}

export default function ServicesGrid() {
  const intro = useSiteContent<ServicesIntroData>('special_services_intro');
  const list = useSiteContent<ServiceItem[]>('special_services_list');

  const loading = intro.loading || list.loading;
  const error = intro.error || list.error;
  const ready = !!intro.data && !!list.data;

  if (loading || error || !ready) {
    return (
      <ContentState
        loading={loading}
        error={error}
        ready={ready}
        onRetry={() => {
          intro.refresh();
          list.refresh();
        }}
      />
    );
  }

  const servicesIntro = intro.data!;
  const services = list.data!;

  return (
    <section id="services" className="w-full bg-background-200 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {servicesIntro.title}{' '}
            <span className="text-primary-500">{servicesIntro.highlight}</span>
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mb-12 md:mb-16">
            {servicesIntro.description}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="group bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                  <i className={`${service.icon} text-xl`} />
                </div>
                <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}